/**
 * A function that validates a value and returns an array of validation results.
 * @param value - The value to validate
 * @param field - Optional field name for the validation result
 * @returns An array of validation results
 */
export interface Validator {
  (value: unknown, field?: string): ValidationResult[]
}

/**
 * Represents a validation error for a single field.
 */
export interface ValidationError {
  /** The name of the field that failed validation */
  field: string
  /** Optional error message describing the validation failure */
  message?: string
  /** The value that failed validation */
  value: unknown
}

/**
 * Represents the result of a validation check for a single field.
 * @extends ValidationError
 */
export interface ValidationResult extends ValidationError {
  /** Whether the validation passed */
  valid: boolean
}

/**
 * Custom error thrown when multiple validation errors occur during batch validation.
 * This error is thrown by functions like `assertShape` and `validateShape` when
 * validation fails and the `soft` option is true.
 */
export class AggregateValidationError extends Error {
  /**
   * Creates a new AggregateValidationError
   * @param errors - Array of individual validation errors
   */
  constructor(
    public readonly errors: ValidationError[]
  ) {
    super('Validation error(s) occurred')
    this.name = 'AggregateValidationError'
  }
}

/**
 * Check if an error is an instance of AggregateValidationError
 * @param error - The error to check
 * @returns True if the error is an instance of AggregateValidationError, false otherwise
 */
export function isAggregateValidationError(error: unknown): error is AggregateValidationError {
  return error instanceof AggregateValidationError
}